/* eslint-disable camelcase */
import { createAsyncThunk } from "@reduxjs/toolkit";

import { QuoteState, QuoteStore, TableCounts, TableStore, ThunkQuoteResponse } from "./types";
import { extraEntityReducersFunction, loadStatus } from "../entitiesType";
import apiCalls from "../../../PowerShadesAPIFunctions";
import { FilterOptions } from "../../ui/types";
import type { HomeTableDatum, HomeTableSet, QuoteShipment } from "../../../powershadesApiTypes";
import { QuoteTypeNamesById } from "../../../powershadesApiTypeExtensions";
import type { AppState } from "../..";
import { ThunkAPI } from "../types";
import { captureSentryError, extractErrorMessage, isFailedApiCall } from "../../../psUtil";
import { setFetchingVisibleQuoteIds } from "../../ui";

const initDatum: () => HomeTableDatum = () => {
	const newDatum: HomeTableDatum = {
		count: 0,
		invoice: 0,
		msrp: 0
	};

	return newDatum;
};

export const initialCountsList = {
	all: initDatum(),
	active_quotes: initDatum(),
	awaiting_po: initDatum(),
	ready_for_manufacturing: initDatum(),
	submitted_to_manufacturing: initDatum(),
	in_production: initDatum(),
	credit_card_declined: initDatum(),
	shipped_orders: initDatum()
};

export const initialQuotesState: QuoteState = {
	list: {},
	counts: {
		list: {
			...initialCountsList
		},
		loadStatus: loadStatus.notLoaded
	},
	types: {
		list: [],
		loadStatus: loadStatus.notLoaded
	},
	loadStatus: loadStatus.notLoaded
};

export const initialQuote = (id = 0): QuoteStore => ({
	loadStatus: loadStatus.notLoaded,
	id,
	ship_order: true,
	msrp_snapshot: 0,
	order_status: "active_quote",
	installation_address_id: 0,
	shipping_address_id: 0,
	pre_ship_whips: false,
	created: 0,
	created_by: "",
	created_by_email: "",
	notes: "",
	quote_type_id: 3,
	reference_number: "",
	title: "",
	updated: 0,
	user_id: 0,
	archived: false,
	display_id: "",
	entity_name: ""
});

export const loadQuoteCounts = createAsyncThunk<TableStore, FilterOptions, ThunkAPI>(
	"entity/quotes/loadQuoteCounts",
	async (filterOptions: FilterOptions, thunkAPI) => {
		try {
			const { getAccessibleQuoteCounts } = apiCalls;
			const quoteCountsResp = await getAccessibleQuoteCounts(filterOptions);
	
			if (isFailedApiCall(quoteCountsResp)) {
				throw quoteCountsResp.error;
			}
			const quoteCounts: TableCounts = quoteCountsResp.data.home_table_count;
	
			const currentItem = (thunkAPI.getState() as AppState).entity.quotes.counts.list;
	
			const formattedCounts: HomeTableSet = {
				all: {
					...currentItem.all,
					count: quoteCounts.all
				},
				active_quotes: {
					...currentItem.active_quotes,
					count: quoteCounts.active_quotes
				},
				awaiting_po: {
					...currentItem.awaiting_po,
					count: quoteCounts.awaiting_po
				},
				ready_for_manufacturing: {
					...currentItem.ready_for_manufacturing,
					count: quoteCounts.ready_for_manufacturing
				},
				submitted_to_manufacturing: {
					...currentItem.submitted_to_manufacturing,
	
					count: quoteCounts.submitted_to_manufacturing
				},
				in_production: {
					...currentItem.in_production,
					count: quoteCounts.in_production
				},
				credit_card_declined: {
					...currentItem.credit_card_declined,
					count: quoteCounts.credit_card_declined
				},
				shipped_orders: {
					...currentItem.shipped_orders,
					count: quoteCounts.shipped_orders
				}
			};
	
			const formattedTableStore = {
				list: formattedCounts,
				loadStatus: loadStatus.fullyLoaded
			};
	
			return formattedTableStore;
		} catch (err: any) {
			captureSentryError(err, {
				tags: {
					section: "redux"
				},
				extra: {
					redux_action: "loadQuoteCounts",
					file_origin: "src/Store/entities/quotes/index.ts"
				}
			});
			const errorMessage = extractErrorMessage(err);
			return thunkAPI.rejectWithValue(errorMessage);
		}
	}
);

export const loadQuoteIds = createAsyncThunk<Record<number, QuoteStore>, void, { rejectValue: string }>(
	"entity/quotes/loadQuoteIds",
	async (_, { rejectWithValue }) => {
		try {
			const { getAccessibleQuoteIds } = apiCalls;

			const quoteIdResp = await getAccessibleQuoteIds();
	
			if (isFailedApiCall(quoteIdResp)) {
				throw quoteIdResp.error;
			}

			const quoteIds = quoteIdResp.data.quote_ids;
	
			const formattedQuotes: Record<number, QuoteStore> = {};
			quoteIds.forEach((id) => {
				formattedQuotes[id] = initialQuote(id);
			});
	
			return formattedQuotes;
		} catch (err: any) {
			captureSentryError(err, {
				tags: {
					section: "redux"
				},
				extra: {
					redux_action: "loadQuoteIds",
					file_origin: "src/Store/entities/quotes/index.ts"
				}
			});
			const errorMessage = extractErrorMessage(err);
			return rejectWithValue(errorMessage);
		}
	}
);

export const loadFilteredQuoteIds = createAsyncThunk<number[], FilterOptions, { rejectValue: string }>(
	"entity/quotes/loadFilteredQuoteIds",
	async ({
		general_filter,
		dealer_ids,
		territory_ids,
		quote_type_ids,
		page_number,
		results_per_page,
		filter_names,
		sort_names
	}, { rejectWithValue }) => {
		try {
			const { getQuotesFiltered } = apiCalls;

			const quoteIdResp = await getQuotesFiltered(
				general_filter,
				dealer_ids,
				territory_ids,
				quote_type_ids,
				page_number,
				results_per_page,
				filter_names,
				sort_names
			);

			if (isFailedApiCall(quoteIdResp)) {
				throw quoteIdResp.error;
			}
	
			const quoteIds = quoteIdResp.data.quote_ids;
	
			return quoteIds;
		} catch (err: any) {
			captureSentryError(err, {
				tags: {
					section: "redux"
				},
				extra: {
					redux_action: "loadFilteredQuoteIds",
					file_origin: "src/Store/entities/quotes/index.ts"
				}
			});
			const errorMessage = extractErrorMessage(err);
			return rejectWithValue(errorMessage);
		}
	}
);

export const loadFilteredHomePageData = createAsyncThunk<
	ThunkQuoteResponse,
	FilterOptions,
	{ rejectValue: string }
>(
	"entity/quotes/loadFilteredHomePageData",
	async (
		{
			general_filter,
			dealer_ids,
			territory_ids,
			quote_type_ids,
			page_number,
			results_per_page,
			filter_names,
			sort_names,
			manufacturer_id
		},
		{ rejectWithValue, dispatch }
	) => {
		try {
			dispatch(setFetchingVisibleQuoteIds(true));
			const quoteIdResp = await apiCalls.getQuotesFiltered(
				general_filter,
				dealer_ids,
				territory_ids,
				quote_type_ids,
				page_number,
				results_per_page,
				filter_names,
				sort_names,
				manufacturer_id
			);
	
			if (isFailedApiCall(quoteIdResp)) {
				throw quoteIdResp.error;
			}
	
			const { quote_ids: quoteIds, home_items } = quoteIdResp.data;

			dispatch(setFetchingVisibleQuoteIds(false));
	
			return {
				quoteIds,
				counts: home_items
			};
		} catch (err: any) {
			captureSentryError(err, {
				tags: {
					section: "redux"
				},
				extra: {
					redux_action: "loadFilteredHomePageData",
					file_origin: "src/Store/entities/quotes/index.ts"
				}
			});
			const errorMessage = extractErrorMessage(err);
			return rejectWithValue(errorMessage);
		}
		
	}
);

export const loadQuotesMetaData = createAsyncThunk<QuoteStore[], number[], { rejectValue: string }>(
	"entity/quotes/loadQuotesMetaData",
	async (quoteIds: number[], { rejectWithValue }) => {
		try {
			const { loadQuoteMetaData } = apiCalls;

			const quoteMetaDataResp = await loadQuoteMetaData(quoteIds);

			if (isFailedApiCall(quoteMetaDataResp)) {
				throw quoteMetaDataResp.error;
			}
	
			const quoteMetaData = quoteMetaDataResp.data.quotes;
	
			const formattedQuotes = quoteMetaData.map((quote) => ({
				...quote,
				loadStatus: loadStatus.metaLoaded
			}));
	
			return formattedQuotes;
		} catch (err: any) {
			captureSentryError(err, {
				tags: {
					section: "redux"
				},
				extra: {
					redux_action: "loadQuotesMetaData",
					file_origin: "src/Store/entities/quotes/index.ts"
				}
			});
			const errorMessage = extractErrorMessage(err);
			return rejectWithValue(errorMessage);
		}
	}
);

export const loadQuoteTypes = createAsyncThunk("entity/quotes/loadQuoteTypes", async (_, { rejectWithValue }) => {
	try {
		const { getQuoteTypes } = apiCalls;

		const quoteTypesResp = await getQuoteTypes();

		if (isFailedApiCall(quoteTypesResp, "quote_types")) {
			console.error("Quote Types API Call Failed");
			console.error({ quoteTypesResp });
			throw quoteTypesResp.error;
		}

		const quoteTypesMap = quoteTypesResp.data.quote_types.map((qt) => ({
			id: qt.id,
			name: qt.name,
			zoho_reference: qt.zoho_reference,
			description: qt.description
		}));

		return quoteTypesMap;
	} catch (err: any) {
		captureSentryError(err, {
			tags: {
				section: "redux"
			},
			extra: {
				redux_action: "loadQuoteTypes",
				file_origin: "src/Store/entities/quotes/index.ts"
			}
		});
		const errorMessage = extractErrorMessage(err);
		return rejectWithValue(errorMessage);
	}
});

export const archiveQuoteThunk = createAsyncThunk(
	"entity/quotes/archive",
	async (quoteId: number, { rejectWithValue }) => {
		try {
			const { archiveQuote } = apiCalls;

			const archiveQuoteResp = await archiveQuote(quoteId);

			if (isFailedApiCall(archiveQuoteResp)) {
				throw archiveQuoteResp.error;
			}

			return archiveQuoteResp.data.success;
		} catch (err: any) {
			captureSentryError(err, {
				tags: {
					section: "redux"
				},
				extra: {
					redux_action: "quotes/archive",
					file_origin: "src/Store/entities/quotes/index.ts"
				}
			});
			const errorMessage = extractErrorMessage(err);
			return rejectWithValue(errorMessage);
		}
	}
);

export const duplicateQuote = createAsyncThunk<number, QuoteStore, { rejectValue: string }>(
	"entity/quotes/duplicate",
	async (quote, { rejectWithValue }) => {
		try {
			const quoteTypeId = quote.quote_type_id ?? 5;
			const quoteType = QuoteTypeNamesById[quoteTypeId] ?? "CEDIA";

			const resp = await apiCalls.duplicateQuote(quote.id, quoteType, []);

			if (isFailedApiCall(resp)) {
				throw resp.error;
			}

			return resp.data.new_quote_id;
		} catch (err: any) {
			captureSentryError(err, {
				tags: {
					section: "redux"
				},
				extra: {
					redux_action: "quotes/duplicate",
					file_origin: "src/Store/entities/quotes/index.ts"
				}
			});
			const errorMessage = extractErrorMessage(err);
			return rejectWithValue(errorMessage);
		}

	}
);

export const loadShipments = createAsyncThunk<QuoteShipment[], number, { rejectValue: string }>(
	"entity/quotes/loadShipments",
	async (quoteId: number, { rejectWithValue }) => {
		let shipments: QuoteShipment[] = [];

		try {
			const resp = await apiCalls.getQuoteShipments(quoteId);

			if (isFailedApiCall(resp)) {
				throw resp.error;
			}

			shipments = resp.data.quote_shipments;
		} catch (err: any) {
			captureSentryError(err, {
				tags: {
					section: "redux"
				},
				extra: {
					redux_action: "quotes/loadShipments",
					file_origin: "src/Store/entities/quotes/index.ts"
				}
			});
			const errorMessage = extractErrorMessage(err);
			return rejectWithValue(errorMessage);
		}

		return shipments;
	}
);

export const loadQuoteById = createAsyncThunk<QuoteStore, number, { rejectValue: string }>(
	"entity/quotes/loadQuoteById",
	async (quoteId: number, { rejectWithValue }) => {
		try {
			const { loadQuoteTwo, getOrderStatus, getQuoteShipments } = apiCalls;
			
			const quoteLoadResponse = await loadQuoteTwo(quoteId);
			const orderStatusResponse = await getOrderStatus(quoteId);
			const shipmentsResponse = await getQuoteShipments(quoteId);

			if (isFailedApiCall(quoteLoadResponse)) {
				throw quoteLoadResponse.error;
			}
			if (isFailedApiCall(orderStatusResponse)) {
				throw orderStatusResponse.error;
			}
			if (isFailedApiCall(shipmentsResponse)) {
				throw shipmentsResponse.error;
			}

			const quoteData = quoteLoadResponse.data.Quote;
			const orderStatus = orderStatusResponse.data.quote_status;
			const shipments = shipmentsResponse.data.quote_shipments;

			const formattedQuote: QuoteStore = {
				...quoteData,
				shipments,
				quote_statuses: orderStatus,
				id: quoteData.ID,
				display_id: `${quoteData.ID}`,
				entity_name: '',
				ship_order: Boolean(quoteData.ship_order),
				pre_ship_whips: Boolean(quoteData.pre_ship_whips),
				loadStatus: loadStatus.fullyLoaded,
				archived: Boolean(quoteData.archived),
				isEvolutionShippingForced: Boolean(quoteData.IsEvolutionShippingForced),
				overrideLightGap: Boolean(quoteData.IsLightgapChoiceActive),
				overrideRailRoad: Boolean(quoteData.IsRailroadPreventionDeactivated)
			};

			return formattedQuote;
		} catch (err: any) {
			captureSentryError(err, {
				tags: {
					section: "redux"
				},
				extra: {
					redux_action: "quotes/loadQuoteById",
					file_origin: "src/Store/entities/quotes/index.ts"
				}
			});
			const errorMessage = extractErrorMessage(err);
			return rejectWithValue(errorMessage);
		}
	}
);

export const quoteBuilder: extraEntityReducersFunction = (builder) => {
	// * Start Fulfilled Promises
	builder.addCase(loadQuoteIds.fulfilled, (state, action) => {
		state.quotes.list = { ...state.quotes.list, ...action.payload };
		state.quotes.loadStatus = loadStatus.needsLoaded;
	});

	builder.addCase(loadQuotesMetaData.fulfilled, (state, action) => {
		action.payload.forEach((quote) => {
			if (state.quotes.list[quote.id]?.loadStatus !== loadStatus.metaLoaded) {
				state.quotes.list[quote.id] = quote;
			}
		});
		state.quotes.loadStatus = loadStatus.metaLoaded;
	});

	builder.addCase(loadQuoteCounts.fulfilled, (state, action) => {
		state.quotes.counts.list = action.payload.list;
		state.quotes.counts.loadStatus = action.payload.loadStatus;
	});

	builder.addCase(loadQuoteTypes.fulfilled, (state, action) => {
		state.quotes.types.list = action.payload;
		state.quotes.types.loadStatus = loadStatus.fullyLoaded;
	});

	builder.addCase(loadShipments.fulfilled, (state, action) => {
		const quote = state.quotes.list[action.meta.arg];
		const shipments = [...action.payload];

		if (quote) {
			quote.shipments = shipments;
		}
		// state.quotes.list[action.meta.arg].shipments = action.payload;
	});
	
	builder.addCase(loadQuoteById.fulfilled, (state, action) => {
		state.quotes.list[action.payload.id] = action.payload;
	});

	// builder.addCase(handleQuoteArchive.fulfilled, (state, action) => {
	// 	if (!action.payload) return;
	// 	const workingQuote = state.quotes.list[action.meta.arg];
	// 	if (!workingQuote) return;
	// 	delete state.quotes.list[action.meta.arg];
	// });

	// * Start Pending Promises
	builder.addCase(loadQuotesMetaData.pending, (state, _) => {
		state.quotes.loadStatus = loadStatus.loading;
	});

	// builder.addCase(loadQuoteCounts.pending, (state, _) => {
	// 	state.quotes.counts.loadStatus = loadStatus.loading;
	// });

	builder.addCase(loadQuoteTypes.pending, (state, _) => {
		state.quotes.types.loadStatus = loadStatus.loading;
	});

	builder.addCase(loadQuoteById.pending, (state, action) => {
		const workingQuote = state.quotes.list[action.meta.arg];
		if (!workingQuote) return;
		workingQuote.loadStatus = loadStatus.loading;
	});
};
